/**
 * TabletSidebar
 *
 * Collapsible sidebar navigation for tablet devices.
 * Shows all locations with unlock status and quick navigation.
 */

import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Home,
  Map,
  Building2,
  Palette,
  FlaskConical,
  Factory,
  TrendingUp,
  Briefcase,
  Calendar,
  Lock,
} from 'lucide-react';
import { CampusLocation } from '../../../types/spatial';
import { useSpatialNavigation } from '../../../hooks/useSpatialNavigation';
import { getAllBuildingConfigs } from '../../../config/locationConfig';

export interface TabletSidebarProps {
  /** Whether the sidebar starts collapsed */
  defaultCollapsed?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get icon component for a location
 */
function getLocationIcon(location: CampusLocation): React.FC<{ className?: string }> {
  switch (location) {
    case CampusLocation.CAMPUS_OVERVIEW:
      return Map;
    case CampusLocation.HEADQUARTERS:
      return Building2;
    case CampusLocation.DESIGN_STUDIO:
      return Palette;
    case CampusLocation.RD_LAB:
      return FlaskConical;
    case CampusLocation.MANUFACTURING:
      return Factory;
    case CampusLocation.SALES_OFFICE:
      return TrendingUp;
    case CampusLocation.EXECUTIVE_OFFICE:
      return Briefcase;
    case CampusLocation.COMPANY_ARCHIVES:
      return Calendar;
    default:
      return Home;
  }
}

export const TabletSidebar: React.FC<TabletSidebarProps> = ({
  defaultCollapsed = false,
  className = '',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const { currentLocation, navigateTo, goToCampus, isUnlocked, isAtCampus } =
    useSpatialNavigation();

  // Get all building configs (excluding campus overview)
  const buildings = getAllBuildingConfigs();

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  return (
    <aside
      className={`fixed left-0 top-0 bottom-0 z-40
                  bg-theme-bg-primary border-r border-theme-border
                  transition-all duration-300 ease-in-out
                  ${isCollapsed ? 'w-16' : 'w-64'}
                  ${className}`}
      aria-label="Location navigation"
    >
      <div className="flex flex-col h-full">
        {/* Header with collapse toggle */}
        <div className="flex items-center justify-between p-4 border-b border-theme-border">
          {!isCollapsed && (
            <h2 className="text-lg font-semibold text-theme-text-primary">
              Locations
            </h2>
          )}
          <button
            onClick={toggleCollapse}
            className="p-2 rounded-lg hover:bg-theme-bg-tertiary
                       text-theme-text-secondary hover:text-theme-text-primary
                       transition-colors"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? (
              <ChevronRight className="w-5 h-5" />
            ) : (
              <ChevronLeft className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-2">
          {/* Campus Overview */}
          <button
            onClick={goToCampus}
            className={`w-full flex items-center gap-3 px-4 py-3
                       transition-colors ${
                         isAtCampus
                           ? 'bg-theme-accent/10 text-theme-accent border-l-2 border-theme-accent'
                           : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-bg-tertiary'
                       }`}
            aria-current={isAtCampus ? 'page' : undefined}
          >
            <Map className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && (
              <span className="text-sm font-medium">Campus Overview</span>
            )}
          </button>

          {/* Divider */}
          <div className="mx-4 my-2 border-t border-theme-border" />

          {/* Building Locations */}
          {buildings.map((config) => {
            const Icon = getLocationIcon(config.id);
            const isCurrent = config.id === currentLocation;
            const unlocked = isUnlocked(config.id);

            return (
              <button
                key={config.id}
                onClick={() => unlocked && navigateTo(config.id)}
                disabled={!unlocked}
                className={`w-full flex items-center gap-3 px-4 py-3
                           transition-colors ${
                             !unlocked
                               ? 'text-theme-text-muted cursor-not-allowed opacity-60'
                               : isCurrent
                               ? 'bg-theme-accent/10 text-theme-accent border-l-2 border-theme-accent'
                               : 'text-theme-text-secondary hover:text-theme-text-primary hover:bg-theme-bg-tertiary'
                           }`}
                aria-current={isCurrent ? 'page' : undefined}
                title={!unlocked ? 'Locked' : config.name}
              >
                <div className="relative flex-shrink-0">
                  <Icon className="w-5 h-5" />
                  {!unlocked && (
                    <Lock className="w-3 h-3 absolute -bottom-1 -right-1 text-theme-text-muted" />
                  )}
                </div>
                {!isCollapsed && (
                  <span className="text-sm font-medium truncate">
                    {config.name}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </aside>
  );
};

export default TabletSidebar;
